import { useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import { useI18n, type Lang } from "../lib/i18n";
import { GUIDE_CONTENT } from "./userGuideData";

interface Section {
  id: string;
  num: number;
  title: string;
  rawText: string;
}

/** Render inline formatting: **bold**, `code`, [text](url), etc. */
function renderInline(text: string): ReactNode {
  const regex = /(\*\*.*?\*\*|`.*?`|\[.*?\]\(.*?\))/g;
  const parts = text.split(regex);

  return parts.map((part, idx) => {
    if (!part) return null;
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <Box component="strong" key={idx} sx={{ fontWeight: 650 }}>
          {part.slice(2, -2)}
        </Box>
      );
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <Box
          component="code"
          key={idx}
          sx={{
            fontFamily: "monospace",
            fontSize: "0.88em",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            px: 0.7,
            py: 0.2,
            borderRadius: 1,
            color: "primary.main",
            fontWeight: 500,
          }}
        >
          {part.slice(1, -1)}
        </Box>
      );
    }
    if (part.startsWith("[") && part.includes("](")) {
      const match = part.match(/\[(.*?)\]\((.*?)\)/);
      if (match) {
        const label = match[1] ?? "";
        const url = match[2] ?? "";
        const isHttp = url.startsWith("http");
        return (
          <Box
            component="a"
            key={idx}
            href={url}
            target={isHttp ? "_blank" : undefined}
            rel={isHttp ? "noopener noreferrer" : undefined}
            sx={{
              color: "primary.main",
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              "&:hover": { color: "primary.dark" },
            }}
          >
            {label}
          </Box>
        );
      }
    }
    return <span key={idx}>{part}</span>;
  });
}

/** Render a markdown table from rows */
function renderTable(lines: string[], keyPrefix: string): ReactNode {
  if (lines.length < 2) return null;
  const headerLine = lines[0] ?? "";
  const headerCols = headerLine
    .split("|")
    .map((c) => c.trim())
    .filter((c, i, arr) => (i > 0 && i < arr.length - 1) || c.length > 0);

  const bodyLines = lines.slice(2);

  return (
    <TableContainer
      key={keyPrefix}
      component={Paper}
      variant="outlined"
      sx={{
        my: 2,
        borderRadius: 2,
        overflowX: "auto",
        boxShadow: "none",
        borderColor: "divider",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "action.hover" : "#f8fafc") }}>
            {headerCols.map((col, idx) => (
              <TableCell key={idx} sx={{ fontWeight: 700, fontSize: "0.85rem", py: 1.2 }}>
                {renderInline(col)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {bodyLines.map((rowLine, rowIdx) => {
            const cells = rowLine
              .split("|")
              .map((c) => c.trim())
              .filter((c, i, arr) => (i > 0 && i < arr.length - 1) || c.length > 0);
            return (
              <TableRow
                key={rowIdx}
                hover
                sx={{
                  "&:nth-of-type(even)": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)",
                  },
                }}
              >
                {cells.map((cell, cIdx) => (
                  <TableCell key={cIdx} sx={{ fontSize: "0.86rem", py: 1 }}>
                    {renderInline(cell)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Render a single section body into React nodes */
function renderSectionBody(text: string, secKey: string): ReactNode[] {
  const lines = text.split("\n");
  const nodes: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const currentLine = lines[i];
    if (currentLine === undefined) {
      i++;
      continue;
    }

    // Subheading ###
    if (currentLine.startsWith("### ")) {
      const title = currentLine.replace(/^###\s+/, "");
      nodes.push(
        <Box key={`${secKey}-h3-${i}`} sx={{ mt: 3, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 4,
              height: 18,
              borderRadius: 1,
              bgcolor: "primary.main",
            }}
          />
          <Typography variant="h6" sx={{ fontSize: "1.05rem", fontWeight: 700 }}>
            {renderInline(title)}
          </Typography>
        </Box>
      );
      i++;
      continue;
    }

    // Markdown Table (starts with |)
    if (currentLine.trim().startsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length) {
        const l = lines[i];
        if (l !== undefined && l.trim().startsWith("|")) {
          tableLines.push(l);
          i++;
        } else {
          break;
        }
      }
      nodes.push(renderTable(tableLines, `${secKey}-tbl-${i}`));
      continue;
    }

    // Blockquote (> ...)
    if (currentLine.trim().startsWith(">")) {
      const quoteLines: string[] = [];
      while (i < lines.length) {
        const l = lines[i];
        if (l !== undefined && l.trim().startsWith(">")) {
          quoteLines.push(l.replace(/^>\s*/, ""));
          i++;
        } else {
          break;
        }
      }
      const quoteContent = quoteLines.join(" ");
      const isWarn =
        quoteContent.includes("⚠️") ||
        quoteContent.toLowerCase().includes("lưu ý") ||
        quoteContent.toLowerCase().includes("warning");
      nodes.push(
        <Alert
          key={`${secKey}-quote-${i}`}
          severity={isWarn ? "warning" : "info"}
          sx={{ my: 1.8, borderRadius: 2, fontSize: "0.88rem" }}
        >
          {renderInline(quoteContent)}
        </Alert>
      );
      continue;
    }

    // Image ![alt](src) or <img ...>
    if (
      (currentLine.includes("![") && currentLine.includes("](")) ||
      currentLine.trim().startsWith("<img")
    ) {
      let src = "";
      let alt = "";
      if (currentLine.includes("![")) {
        const m = currentLine.match(/!\[(.*?)\]\((.*?)\)/);
        if (m) {
          alt = m[1] ?? "";
          src = m[2] ?? "";
        }
      } else {
        const mSrc = currentLine.match(/src=["'](.*?)["']/);
        const mAlt = currentLine.match(/alt=["'](.*?)["']/);
        if (mSrc) src = mSrc[1] ?? "";
        if (mAlt) alt = mAlt[1] ?? "";
      }

      if (src) {
        nodes.push(
          <Box
            key={`${secKey}-img-${i}`}
            sx={{
              my: 2.5,
              textAlign: "center",
              p: 1.5,
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(0,0,0,0.3)" : "#f8fafc"),
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Box
              component="img"
              src={src}
              alt={alt}
              sx={{
                maxWidth: "100%",
                maxHeight: 460,
                borderRadius: 1.5,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            />
            {alt ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {alt}
              </Typography>
            ) : null}
          </Box>
        );
      }
      i++;
      continue;
    }

    // Bullet list (- or * or •)
    if (/^\s*[-*•]\s+/.test(currentLine)) {
      const listItems: string[] = [];
      while (i < lines.length) {
        const l = lines[i];
        if (l !== undefined && /^\s*[-*•]\s+/.test(l)) {
          listItems.push(l.replace(/^\s*[-*•]\s+/, ""));
          i++;
        } else {
          break;
        }
      }
      nodes.push(
        <Box
          component="ul"
          key={`${secKey}-ul-${i}`}
          sx={{
            m: 0,
            my: 1,
            pl: 3,
            "& li": {
              mb: 0.6,
              fontSize: "0.92rem",
              lineHeight: 1.6,
            },
          }}
        >
          {listItems.map((li, liIdx) => (
            <li key={liIdx}>{renderInline(li)}</li>
          ))}
        </Box>
      );
      continue;
    }

    // Numbered list (1. 2. etc)
    if (/^\s*\d+\.\s+/.test(currentLine)) {
      const listItems: string[] = [];
      while (i < lines.length) {
        const l = lines[i];
        if (l !== undefined && /^\s*\d+\.\s+/.test(l)) {
          listItems.push(l.replace(/^\s*\d+\.\s+/, ""));
          i++;
        } else {
          break;
        }
      }
      nodes.push(
        <Box
          component="ol"
          key={`${secKey}-ol-${i}`}
          sx={{
            m: 0,
            my: 1,
            pl: 3,
            "& li": {
              mb: 0.6,
              fontSize: "0.92rem",
              lineHeight: 1.6,
            },
          }}
        >
          {listItems.map((li, liIdx) => (
            <li key={liIdx}>{renderInline(li)}</li>
          ))}
        </Box>
      );
      continue;
    }

    // Code block ```...```
    if (currentLine.trim().startsWith("```")) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length) {
        const l = lines[i];
        if (l !== undefined && !l.trim().startsWith("```")) {
          codeLines.push(l);
          i++;
        } else {
          break;
        }
      }
      i++; // skip closing ```
      nodes.push(
        <Box
          key={`${secKey}-code-${i}`}
          component="pre"
          sx={{
            my: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: (theme) => (theme.palette.mode === "dark" ? "#1e1e1e" : "#2d3748"),
            color: "#e2e8f0",
            overflowX: "auto",
            fontSize: "0.85rem",
            fontFamily: "monospace",
          }}
        >
          {codeLines.join("\n")}
        </Box>
      );
      continue;
    }

    // Regular paragraph
    const trimmed = currentLine.trim();
    if (trimmed) {
      nodes.push(
        <Typography
          key={`${secKey}-p-${i}`}
          variant="body2"
          sx={{
            my: 0.8,
            fontSize: "0.92rem",
            lineHeight: 1.65,
            color: "text.primary",
          }}
        >
          {renderInline(trimmed)}
        </Typography>
      );
    }
    i++;
  }

  return nodes;
}

export function UserGuide() {
  const { lang: cmsLang, t } = useI18n();
  const [selectedLang, setSelectedLang] = useState<Lang>(cmsLang);
  const [search, setSearch] = useState("");
  const [activeSectionId, setActiveSectionId] = useState<string>("sec-1");
  const topRef = useRef<HTMLDivElement>(null);

  // Sync with CMS language if user changes it externally
  useEffect(() => {
    setSelectedLang(cmsLang);
  }, [cmsLang]);

  // Parse markdown into 13 sections
  const sections: Section[] = useMemo(() => {
    const raw = GUIDE_CONTENT[selectedLang] || GUIDE_CONTENT.vi;
    const parts = raw.split(/\n(?=## )/);

    return parts.map((part, idx) => {
      const lines = part.split("\n");
      const firstLine = lines[0] ?? "";
      const titleLine = firstLine.replace(/^##\s+/, "").trim();
      const rawText = lines.slice(1).join("\n").trim();
      const numMatch = titleLine.match(/^(\d+)\./);
      const numStr = numMatch?.[1];
      const num = numStr ? parseInt(numStr, 10) : idx + 1;

      return {
        id: `sec-${num}`,
        num,
        title: titleLine,
        rawText,
      };
    });
  }, [selectedLang]);

  // Filter sections by search query
  const filteredSections = useMemo(() => {
    if (!search.trim()) return sections;
    const query = search.toLowerCase();
    return sections.filter(
      (s) =>
        s.title.toLowerCase().includes(query) ||
        s.rawText.toLowerCase().includes(query)
    );
  }, [sections, search]);

  const scrollToSection = (id: string) => {
    setActiveSectionId(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box ref={topRef} sx={{ maxWidth: 1400, mx: "auto", pb: 6 }}>
      {/* Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 3,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))"
              : "linear-gradient(135deg, #eff6ff, #f0fdf4)",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          sx={{
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 0.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  color: "#fff",
                }}
              >
                <MenuBookIcon fontSize="small" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: -0.3 }}>
                {t("guide.title")}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t("guide.subtitle")}
            </Typography>
          </Box>

          {/* Language Switcher */}
          <ToggleButtonGroup
            value={selectedLang}
            exclusive
            size="small"
            onChange={(_, val) => val && setSelectedLang(val)}
            sx={{
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : "#fff"),
            }}
          >
            <ToggleButton value="vi" sx={{ px: 1.8, py: 0.6, fontSize: "0.85rem", fontWeight: 600 }}>
              🇻🇳 Tiếng Việt
            </ToggleButton>
            <ToggleButton value="en" sx={{ px: 1.8, py: 0.6, fontSize: "0.85rem", fontWeight: 600 }}>
              🇬🇧 English
            </ToggleButton>
            <ToggleButton value="zh" sx={{ px: 1.8, py: 0.6, fontSize: "0.85rem", fontWeight: 600 }}>
              🇨🇳 中文
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Search bar */}
        <Box sx={{ mt: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("guide.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
            sx={{
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : "#fff"),
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </Paper>

      {/* Main Content Layout with Sticky TOC Sidebar */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "300px 1fr" }, gap: 3 }}>
        {/* Table of Contents - Sidebar */}
        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            position: "sticky",
            top: 24,
            height: "fit-content",
            maxHeight: "calc(100vh - 48px)",
            overflowY: "auto",
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2.5,
              borderColor: "divider",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "background.paper" : "#fafafa",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, px: 1 }}>
              {t("guide.toc")} ({sections.length})
            </Typography>
            <Stack spacing={0.5}>
              {sections.map((sec) => {
                const isActive = activeSectionId === sec.id;
                return (
                  <Button
                    key={sec.id}
                    size="small"
                    onClick={() => scrollToSection(sec.id)}
                    sx={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      py: 0.8,
                      px: 1.2,
                      borderRadius: 1.5,
                      fontSize: "0.82rem",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? "primary.main" : "text.secondary",
                      bgcolor: isActive
                        ? (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(59, 130, 246, 0.15)"
                              : "rgba(59, 130, 246, 0.08)"
                        : "transparent",
                      "&:hover": {
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.04)",
                        color: "primary.main",
                      },
                    }}
                  >
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {sec.title}
                    </Box>
                  </Button>
                );
              })}
            </Stack>
          </Paper>
        </Box>

        {/* Section List / Articles */}
        <Box>
          {filteredSections.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t("guide.noResults")}
            </Alert>
          ) : (
            <Stack spacing={3}>
              {filteredSections.map((sec) => (
                <Card
                  key={sec.id}
                  id={sec.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    borderColor: "divider",
                    transition: "box-shadow 0.2s, border-color 0.2s",
                    "&:hover": {
                      borderColor: "primary.light",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 4px 20px rgba(0,0,0,0.4)"
                          : "0 4px 20px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                    {/* Section Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 2,
                        mb: 2.5,
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                        <Chip
                          label={`#${sec.num}`}
                          size="small"
                          color="primary"
                          variant="filled"
                          sx={{ fontWeight: 700, borderRadius: 1.5 }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: "1.05rem", sm: "1.2rem" } }}>
                          {sec.title.replace(/^\d+\.\s*/, "")}
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Section Body */}
                    <Box>{renderSectionBody(sec.rawText, sec.id)}</Box>
                  </CardContent>
                </Card>
              ))}

              {/* Back to top button */}
              <Box sx={{ textAlign: "center", pt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowUpwardIcon />}
                  onClick={scrollToTop}
                  sx={{ borderRadius: 2 }}
                >
                  {t("guide.backToTop")}
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default UserGuide;
